/**
 * Firestore reads, as React hooks.
 *
 * Everything is a live subscription rather than a fetch, and that is the point of the
 * whole dashboard: log a set in WhatsApp with the page open and it appears here without
 * a refresh. Verifying the bot is the job (D-57), so the shortest possible loop between
 * "sent the message" and "saw the result" is the feature.
 */

import { createContext, useContext, useEffect, useState } from "react";
import { collection, doc, getDocs, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import type { CatalogEdits, CustomExercise, DateString, GymRef, Plan, UserProfile, Workout } from "@altrtrack/core";
import { installCatalog, isPlan, resetCatalog } from "@altrtrack/core";
import { db } from "./firebase.js";

export interface Loadable<T> {
  data: T | null;
  loading: boolean;
  /** A rules denial or a dropped connection. Shown, never swallowed. */
  error: string | null;
}

export function useProfile(uid: string | null): Loadable<UserProfile> {
  const [state, setState] = useState<Loadable<UserProfile>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!uid) return;
    setState({ data: null, loading: true, error: null });
    return onSnapshot(
      doc(db, "users", uid),
      (snap) =>
        setState({
          data: snap.exists() ? (snap.data() as UserProfile) : null,
          loading: false,
          error: null,
        }),
      (error) => setState({ data: null, loading: false, error: error.message }),
    );
  }, [uid]);

  return state;
}

/**
 * The saved training plan (B-48), or null when there is none.
 *
 * A document that is present but not a plan this code can read is also null — Today
 * falls back to last week's session rather than crashing on it — and `error` says
 * "unreadable" so Profile can say so and offer to make a new one.
 */
export function usePlan(uid: string | null): Loadable<Plan | null> {
  const [state, setState] = useState<Loadable<Plan | null>>({ data: null, loading: true, error: null });

  useEffect(() => {
    if (!uid) return;
    setState({ data: null, loading: true, error: null });
    return onSnapshot(
      doc(db, "users", uid, "plan", "current"),
      (snap) => {
        if (!snap.exists()) return setState({ data: null, loading: false, error: null });
        const value = snap.data();
        setState(isPlan(value) ? { data: value, loading: false, error: null } : { data: null, loading: false, error: "unreadable" });
      },
      (error) => setState({ data: null, loading: false, error: error.message }),
    );
  }, [uid]);

  return state;
}

/**
 * Every workout, newest first, as Firestore returns them. Today looks its own date up in
 * this; Progress's ledger (`daysInRange`) filters and re-sorts within whatever range is
 * selected rather than relying on this order.
 */
export function useWorkouts(uid: string | null): Loadable<Workout[]> {
  const [state, setState] = useState<Loadable<Workout[]>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!uid) return;
    setState({ data: null, loading: true, error: null });
    return onSnapshot(
      query(collection(db, "users", uid, "workouts"), orderBy("date", "desc")),
      (snap) =>
        setState({
          data: snap.docs.map((d) => d.data() as Workout),
          loading: false,
          error: null,
        }),
      (error) => setState({ data: null, loading: false, error: error.message }),
    );
  }, [uid]);

  return state;
}

/**
 * Today, as a "YYYY-MM-DD" string in the user's own timezone.
 *
 * The same `formatToParts` approach as `functions/src/time.ts`, and for the same reason:
 * a locale whose output merely looks ISO is not a contract. Core never asks what day it
 * is (C-13) — it is told, here and there.
 */
export function localToday(timezone: string, now = Date.now()): DateString {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(now));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("year").padStart(4, "0")}-${get("month").padStart(2, "0")}-${get("day").padStart(2, "0")}`;
}

/**
 * The gyms listed at one PIN (B-52). A one-off read, not a subscription: the list is
 * consulted while picking and a gym added elsewhere mid-pick changes nothing that
 * matters. Sorted here rather than with `orderBy`, which would need a composite index.
 */
export async function findGyms(pinCode: string): Promise<GymRef[]> {
  const snap = await getDocs(query(collection(db, "gyms"), where("pinCode", "==", pinCode), limit(50)));
  return snap.docs
    .map((d) => ({ id: d.id, name: String(d.get("name")), pinCode: String(d.get("pinCode")) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * The admin's exercise-list edits (B-66, D-118), installed over the built-in catalog as
 * they change, live like everything else here: an exercise added on one phone appears in
 * the picker on another without a refresh.
 *
 * Returns a revision that moves on every install. Anything that memoises over `CATALOG`
 * — the picker's list, Profile's factors table — puts it in its dependencies, through
 * `useCatalogRevision`, because the array it reads is the same object before and after.
 *
 * Until both collections have answered, the built-ins alone are in use; a logged set
 * against an added exercise shows under its stored name meanwhile and scores 0 for that
 * moment, exactly as an unknown id always has (D-34).
 */
export function useCatalogEdits(uid: string | null): { revision: number; edits: CatalogEdits } {
  const [added, setAdded] = useState<CustomExercise[] | null>(null);
  const [renamed, setRenamed] = useState<Record<string, string> | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!uid) return;
    const stopAdded = onSnapshot(
      collection(db, "customExercises"),
      (snap) => setAdded(snap.docs.map((d) => ({ ...(d.data() as Omit<CustomExercise, "id">), id: d.id }))),
      () => setAdded([]),
    );
    const stopNames = onSnapshot(
      collection(db, "exerciseNames"),
      (snap) => setRenamed(Object.fromEntries(snap.docs.map((d) => [d.id, d.get("name") as string]))),
      () => setRenamed({}),
    );
    return () => {
      stopAdded();
      stopNames();
      resetCatalog();
    };
  }, [uid]);

  const edits: CatalogEdits = { added: added ?? [], renamed: renamed ?? {} };
  useEffect(() => {
    if (added === null || renamed === null) return;
    installCatalog({ added, renamed });
    setRevision((r) => r + 1);
  }, [added, renamed]);

  return { revision, edits };
}

export const CatalogContext = createContext<{ revision: number; edits: CatalogEdits }>({
  revision: 0,
  edits: { added: [], renamed: {} },
});

/** See `useCatalogEdits`. */
export function useCatalogRevision(): number {
  return useContext(CatalogContext).revision;
}

/** The edits as stored, for the admin card — see `useCatalogEdits`. */
export function useStoredCatalogEdits(): CatalogEdits {
  return useContext(CatalogContext).edits;
}
